/**
 * Utility functions for localStorage operations
 */

/**
 * Get cached player emoji from localStorage
 * @returns {string|null} The cached emoji or null if not found/error
 */
export const getCachedPlayerEmoji = () => {
  try {
    return localStorage.getItem('pumpAndDump_playerEmoji');
  } catch (e) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('Failed to read cached emoji:', e);
    }
    return null;
  }
};

/**
 * Get cached player name from localStorage
 * @returns {string} The cached name or empty string if not found/error
 */
export const getCachedPlayerName = () => {
  try {
    return localStorage.getItem('pumpAndDump_playerName') || "";
  } catch (e) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('Failed to read cached player name:', e);
    }
    return "";
  }
};

/**
 * Cache player emoji to localStorage
 * @param {string} emoji - The emoji to cache
 */
export const cachePlayerEmoji = (emoji) => {
  if (!emoji) return;
  try {
    localStorage.setItem('pumpAndDump_playerEmoji', emoji);
  } catch (e) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('Failed to cache player emoji:', e);
    }
  }
};

/**
 * Cache player name to localStorage
 * @param {string} name - The name to cache
 */
export const cachePlayerName = (name) => {
  if (!name || !name.trim()) return;
  try {
    localStorage.setItem('pumpAndDump_playerName', name.trim());
  } catch (e) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('Failed to cache player name:', e);
    }
  }
};

/**
 * Get cached last joined room name from localStorage
 * @returns {string} The cached room name or empty string if not found/error
 */
export const getCachedRoomName = () => {
  try {
    return localStorage.getItem('pumpAndDump_lastRoomName') || "";
  } catch (e) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('Failed to read cached room name:', e);
    }
    return "";
  }
};

/**
 * Cache last joined room name to localStorage
 * @param {string} roomName - The room name to cache
 */
export const cacheRoomName = (roomName) => {
  if (!roomName || !roomName.trim()) return;
  try {
    localStorage.setItem('pumpAndDump_lastRoomName', roomName.trim());
  } catch (e) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('Failed to cache room name:', e);
    }
  }
};

const ROOM_KEY = 'pumpAndDump_lastRoomName';

export const clearCachedRoomName = () => {
  try {
    localStorage.removeItem(ROOM_KEY);
  } catch (error) {
    console.error('Error clearing room name from localStorage:', error);
  }
};

// Active game session management
const ACTIVE_GAME_KEY = 'pumpndump_active_game_session';

export const saveActiveGameSession = (room, name) => {
  try {
    const session = {
      room,
      name,
      timestamp: Date.now()
    };
    localStorage.setItem(ACTIVE_GAME_KEY, JSON.stringify(session));
  } catch (error) {
    console.error('Error saving active game session:', error);
  }
};

export const getActiveGameSession = () => {
  try {
    const sessionData = localStorage.getItem(ACTIVE_GAME_KEY);
    if (!sessionData) return null;
    
    const session = JSON.parse(sessionData);
    
    // Check if session is less than 24 hours old
    const ONE_DAY = 24 * 60 * 60 * 1000;
    if (Date.now() - session.timestamp > ONE_DAY) {
      clearActiveGameSession();
      return null;
    }
    
    return session;
  } catch (error) {
    console.error('Error getting active game session:', error);
    return null;
  }
};

export const clearActiveGameSession = () => {
  try {
    localStorage.removeItem(ACTIVE_GAME_KEY);
  } catch (error) {
    console.error('Error clearing active game session:', error);
  }
};

// Tutorial completion tracking
const TUTORIAL_SEEN_KEY = 'pumpAndDump_tutorialSeen';

/**
 * Check if player has seen the tutorial before
 * @returns {boolean} True if player has seen tutorial, false if new player
 */
export const hasSeenTutorial = () => {
  try {
    const seen = localStorage.getItem(TUTORIAL_SEEN_KEY);
    return seen === 'true';
  } catch (e) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('Failed to read tutorial seen status:', e);
    }
    return false; // Default to new player if can't read
  }
};

/**
 * Mark tutorial as seen (player is no longer new)
 */
export const markTutorialSeen = () => {
  try {
    localStorage.setItem(TUTORIAL_SEEN_KEY, 'true');
  } catch (e) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('Failed to mark tutorial as seen:', e);
    }
  }
};
